import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

const schema = z.object({
  checkIn: z.date().optional(),
  checkOut: z.date().optional(),
  guests: z.string().min(1).default('1'),
});

export type BaseSearchState = z.infer<typeof schema>;

export interface UseBaseSearchFormOptions {
  initialCheckIn?: Date;
  initialCheckOut?: Date;
  initialGuests?: string;
  onDateChange?: (
    checkIn: Date | undefined,
    checkOut: Date | undefined,
  ) => void;
  onGuestsChange?: (guests: string) => void;
}

export const useBaseSearchForm = ({
  initialCheckIn,
  initialCheckOut,
  initialGuests = '1',
  onDateChange,
  onGuestsChange,
}: UseBaseSearchFormOptions = {}) => {
  const [checkIn, setCheckIn] = useState<Date | undefined>(initialCheckIn);
  const [checkOut, setCheckOut] = useState<Date | undefined>(initialCheckOut);
  const [guests, setGuests] = useState<string>(initialGuests);

  useEffect(() => {
    if (initialCheckIn !== undefined) setCheckIn(initialCheckIn);
  }, [initialCheckIn]);

  useEffect(() => {
    if (initialCheckOut !== undefined) setCheckOut(initialCheckOut);
  }, [initialCheckOut]);

  useEffect(() => {
    if (initialGuests !== undefined) setGuests(initialGuests);
  }, [initialGuests]);

  const hasValidDateRange = useMemo(() => {
    if (!checkIn || !checkOut) return false;
    return checkIn < checkOut;
  }, [checkIn, checkOut]);

  const handleCheckIn = (date: Date | undefined) => {
    if (checkOut && date && checkOut <= date) {
      setCheckOut(undefined);
      setCheckIn(date);
      onDateChange?.(date, undefined);
      return;
    }
    setCheckIn(date);
    onDateChange?.(date, checkOut);
  };

  const handleCheckOut = (date: Date | undefined) => {
    setCheckOut(date);
    onDateChange?.(checkIn, date);
  };

  const handleGuests = (value: string) => {
    setGuests(value);
    onGuestsChange?.(value);
  };

  return {
    // state
    checkIn,
    checkOut,
    guests,
    hasValidDateRange,
    // actions
    handleCheckIn,
    handleCheckOut,
    handleGuests,
    // utility
    setCheckIn,
    setCheckOut,
    setGuests,
  };
};
